import React, { useState, useCallback, useEffect } from "react";
import { View } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AppTextInput } from "@/components/ui/TextInput";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { SelectField } from "@/components/ui/SelectField";
import { SuccessState } from "@/components/ui/SuccessState";
import { ErrorState } from "@/components/ui/ErrorState";
import { useI18n } from "@/context/I18nContext";
import { usersApi, type AdminUserDetail } from "@/lib/api/users";
import type { StaffRoleType } from "@/constants";
import type { CountryOption } from "@/components/ui/PhoneInput";

type ModalState = "form" | "success" | "error";

export interface UserModalProps {
  visible: boolean;
  onDismiss: () => void;
  /** Called after a successful create/update so the caller can refresh. */
  onSuccess?: () => void;
  /** When provided, the modal is in edit mode and the form is pre-populated. */
  user?: AdminUserDetail;
}

const ROLE_OPTIONS: { labelKey: string; value: StaffRoleType }[] = [
  { labelKey: "roles.superAdmin", value: "super_admin" },
  { labelKey: "roles.admin", value: "admin" },
  { labelKey: "roles.staff", value: "staff" },
];

export function UserModal({ visible, onDismiss, onSuccess, user }: UserModalProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const isEdit = !!user;

  const [modalState, setModalState] = useState<ModalState>("form");
  const [submitting, setSubmitting] = useState(false);

  // Phone state
  const [dialCode, setDialCode] = useState("+966");
  const [countryFlag, setCountryFlag] = useState("🇸🇦");
  const [phone, setPhone] = useState("");

  // Form fields
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<string>("");

  // Validation errors
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [roleError, setRoleError] = useState<string | undefined>(undefined);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
  const [firstNameError, setFirstNameError] = useState<string | undefined>(undefined);
  const [lastNameError, setLastNameError] = useState<string | undefined>(undefined);

  // Pre-populate fields in edit mode when modal opens
  useEffect(() => {
    if (visible && isEdit && user) {
      const [code, ...rest] = user.phone?.startsWith("+")
        ? [
            user.phone.slice(0, user.phone.indexOf(" ") > -1 ? user.phone.indexOf(" ") : 4),
            user.phone.slice(user.phone.indexOf(" ") > -1 ? user.phone.indexOf(" ") + 1 : 4),
          ]
        : ["+966", [user.phone ?? ""]];
      setDialCode(code);
      setPhone(rest.join(""));
      setEmail(user.email ?? "");
      setFirstName(user.firstName ?? "");
      setLastName(user.lastName ?? "");
      setRole(user.role ?? "");
    }
  }, [visible, isEdit, user]);

  const resetForm = useCallback(() => {
    setModalState("form");
    setSubmitting(false);
    setDialCode("+966");
    setCountryFlag("🇸🇦");
    setPhone("");
    setEmail("");
    setFirstName("");
    setLastName("");
    setRole("");
    setEmailError(undefined);
    setRoleError(undefined);
    setPhoneError(undefined);
    setFirstNameError(undefined);
    setLastNameError(undefined);
  }, []);

  const handleDismiss = useCallback(() => {
    resetForm();
    onDismiss();
  }, [resetForm, onDismiss]);

  const handleCountrySelect = useCallback((country: CountryOption) => {
    setDialCode(country.dialCode);
    setCountryFlag(country.flag);
  }, []);

  const validate = useCallback(() => {
    let valid = true;

    if (!phone.trim()) {
      setPhoneError("common.required");
      valid = false;
    } else {
      setPhoneError(undefined);
    }

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setEmailError("common.required");
      valid = false;
    } else {
      setEmailError(undefined);
    }

    if (!role) {
      setRoleError("common.required");
      valid = false;
    } else {
      setRoleError(undefined);
    }

    if (!isEdit) {
      if (!firstName.trim()) {
        setFirstNameError("common.required");
        valid = false;
      } else {
        setFirstNameError(undefined);
      }

      if (!lastName.trim()) {
        setLastNameError("common.required");
        valid = false;
      } else {
        setLastNameError(undefined);
      }
    }

    return valid;
  }, [phone, email, role, firstName, lastName, isEdit]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      if (isEdit && user) {
        await usersApi.updateUser(user.id, {
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          phone: phone.trim() || undefined,
          dialCode,
          role: role as StaffRoleType,
        });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["users", "list"] }),
          queryClient.invalidateQueries({ queryKey: ["users", "detail", user.id] }),
          queryClient.invalidateQueries({ queryKey: ["users", "activityLog", user.id] }),
          queryClient.invalidateQueries({ queryKey: ["admin", "stats"] }),
        ]);
      } else {
        await usersApi.createUser({
          email: email.trim(),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          phone: phone.trim() || undefined,
          dialCode,
          role: role as StaffRoleType,
        });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["users", "list"] }),
          queryClient.invalidateQueries({ queryKey: ["admin", "stats"] }),
        ]);
      }
      setModalState("success");
      onSuccess?.();
    } catch {
      setModalState("error");
    } finally {
      setSubmitting(false);
    }
  }, [validate, isEdit, user, firstName, lastName, phone, dialCode, email, role, onSuccess]);

  const roleOptions = ROLE_OPTIONS.map((opt) => ({
    label: t(opt.labelKey as any),
    value: opt.value,
  }));

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      closeOnBackdrop={!submitting}
      title={isEdit ? "staff.users.modal.editTitle" : "staff.users.modal.addTitle"}
      footer={
        modalState === "form" ? (
          <View className="flex-row gap-4 justify-end">
            <Button
              label="common.cancel"
              onPress={handleDismiss}
              variant="tertiary"
              disabled={submitting}
            />
            <Button
              label={isEdit ? "staff.users.modal.saveButton" : "staff.users.modal.addButton"}
              onPress={handleSubmit}
              variant="primary"
              loading={submitting}
            />
          </View>
        ) : null
      }
    >
      {modalState === "form" && (
        <View className="gap-4 w-full">
          {/* Country + Phone Number row */}
          <PhoneInput
            label="staff.users.detail.phoneNumber"
            dialCode={dialCode}
            countryFlag={countryFlag}
            onCountrySelect={handleCountrySelect}
            value={phone}
            onChangeText={(v) => {
              setPhone(v);
              setPhoneError(undefined);
            }}
            keyboardType="phone-pad"
            required
            error={phoneError as any}
          />

          {/* Email Address */}
          <AppTextInput
            label="staff.users.detail.emailAddress"
            placeholder="staff.invitations.send.emailPlaceholder"
            value={email}
            onChangeText={(v) => {
              setEmail(v);
              setEmailError(undefined);
            }}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            required
            editable={!isEdit}
            error={emailError as any}
          />

          {/* First Name + Last Name row */}
          <View className="flex-row gap-3">
            <View className="flex-1">
              <AppTextInput
                label="staff.users.detail.firstName"
                placeholder="staff.users.detail.firstName"
                value={firstName}
                onChangeText={setFirstName}
                autoCapitalize="words"
                error={firstNameError as any}
              />
            </View>
            <View className="flex-1">
              <AppTextInput
                label="staff.users.detail.lastName"
                placeholder="staff.users.detail.lastName"
                value={lastName}
                onChangeText={setLastName}
                autoCapitalize="words"
                error={lastNameError as any}
              />
            </View>
          </View>

          {/* Role */}
          <SelectField
            label="staff.users.detail.role"
            options={roleOptions}
            value={role}
            onChange={(v) => {
              setRole(v);
              setRoleError(undefined);
            }}
            placeholder="staff.users.modal.rolePlaceholder"
            required
            error={roleError as any}
          />
        </View>
      )}

      {modalState === "success" && (
        <SuccessState
          title={isEdit ? "staff.users.modal.editSuccess" : "staff.users.modal.addSuccess"}
          description={
            isEdit ? "staff.users.modal.editSuccessBody" : "staff.users.modal.addSuccessBody"
          }
          className="px-0 pt-0"
        />
      )}

      {modalState === "error" && (
        <ErrorState
          description="common.error.generic"
          className="px-0 pt-0"
          onRetry={() => setModalState("form")}
        />
      )}
    </Modal>
  );
}
